"use client";

import { proseMathLines } from "@/lib/proseMath";
import { splitWideArrays } from "@/lib/splitWideArray";
import { useMemo, useDeferredValue, useRef } from "react";
import type { ReactNode } from "react";
import { unified } from "unified";

import { useScaleDisplayMath } from "@/hooks/useScaleDisplayMath";

// remark plugins
import remarkParse from "remark-parse";
import remarkMath from "remark-math";
import remarkGfm from "remark-gfm";

// rehype plugins
import remarkRehype from "remark-rehype";
import rehypeRaw from "rehype-raw"; // ← parse raw HTML back into the tree
import rehypeKatex from "rehype-katex"; // ← turn math nodes → KaTeX HTML
import rehypeReact from "rehype-react";

import * as prod from "react/jsx-runtime";
import "katex/dist/katex.min.css";

import "@/chatstyling.css";

const production = { Fragment: prod.Fragment, jsx: prod.jsx, jsxs: prod.jsxs };

// Column/row spans: GFM tables and KaTeX have no \multicolumn, \multirow or
// \cline, so they used to reach the student as raw LaTeX or a KaTeX error.
// \multicolumn{n}{spec}{x} becomes x plus n-1 empty cells (the GFM table gets a
// marker so applyLatexColspan can merge them back into one colspan cell),
// \multirow keeps only its content, and \cline (a partial rule) is dropped.
// The marker is "\uE000<n>\uE001" (Unicode private-use characters, never in book text).
const COLSPAN_MARK = /^\uE000(\d+)\uE001/;

function skipSpace(s: string, i: number): number {
  while (i < s.length && /\s/.test(s[i])) i++;
  return i;
}

function readOptional(s: string, i: number): number {
  const j = skipSpace(s, i);
  if (s[j] !== "[") return i;
  const end = s.indexOf("]", j);
  return end === -1 ? i : end + 1;
}

function readGroup(s: string, i: number): [string, number] | null {
  const start = skipSpace(s, i);
  if (s[start] !== "{") return null;
  let depth = 0;
  for (let j = start; j < s.length; j++) {
    if (s[j] === "\\") {
      j++;
      continue;
    }
    if (s[j] === "{") depth++;
    else if (s[j] === "}" && --depth === 0)
      return [s.slice(start + 1, j), j + 1];
  }
  return null;
}

export function expandLatexSpans(
  src: string,
  mode: "markdown-cell" | "math-cell" | "content-only"
): string {
  const noRules = src.replace(/\\(?:cline|hhline)\s*\{[^}]*\}/g, "");
  const re = /\\(multicolumn|multirow)(?![a-zA-Z])/g;
  let out = "";
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(noRules))) {
    let j = m.index + m[0].length;
    if (m[1] === "multirow") j = readOptional(noRules, j);
    const count = readGroup(noRules, j);
    if (!count) continue;
    j = m[1] === "multirow" ? readOptional(noRules, count[1]) : count[1];
    const spec = readGroup(noRules, j);
    if (!spec) continue;
    j = m[1] === "multirow" ? readOptional(noRules, spec[1]) : spec[1];
    // A missing content group (seen in book copy) counts as an empty cell.
    const body = readGroup(noRules, j) ?? ["", j];
    const n = parseInt(count[0].trim(), 10);
    let replacement = expandLatexSpans(body[0], "content-only").trim();
    if (m[1] === "multicolumn" && n > 1 && mode !== "content-only") {
      const mark = mode === "markdown-cell" ? `\uE000${n}\uE001` : "";
      replacement = `${mark}${replacement}${" &".repeat(n - 1)}`;
    }
    out += noRules.slice(last, m.index) + replacement;
    last = body[1];
    re.lastIndex = body[1];
  }
  return out + noRules.slice(last);
}

// rehype step: a cell that starts with the colspan marker absorbs the n-1
// empty cells after it.
export function applyLatexColspan(tree: any) {
  const isCell = (n: any) =>
    n && n.type === "element" && (n.tagName === "td" || n.tagName === "th");
  const textOf = (n: any): string =>
    n.type === "text" ? n.value : (n.children || []).map(textOf).join("");
  const firstText = (n: any): any => {
    if (n.type === "text") return n;
    for (const c of n.children || []) {
      const t = firstText(c);
      if (t) return t;
    }
    return null;
  };
  const walk = (node: any) => {
    if (!node || typeof node !== "object") return;
    if (node.type === "element" && node.tagName === "tr") {
      const kids = node.children || [];
      for (let i = 0; i < kids.length; i++) {
        if (!isCell(kids[i])) continue;
        const t = firstText(kids[i]);
        const mk =
          t && typeof t.value === "string" ? t.value.match(COLSPAN_MARK) : null;
        if (!mk) continue;
        t.value = t.value.slice(mk[0].length);
        let span = 1;
        let j = i + 1;
        while (span < Number(mk[1]) && j < kids.length) {
          if (!isCell(kids[j])) {
            j++;
            continue;
          }
          if (textOf(kids[j]).trim() !== "") break;
          kids.splice(j, 1);
          span++;
        }
        if (span > 1)
          kids[i].properties = { ...(kids[i].properties || {}), colSpan: span };
      }
    }
    for (const c of node.children || []) walk(c);
  };
  walk(tree);
}

export function convertLatexTablesToMarkdown(md: string): string {
  // Match LaTeX table environments (with or without $$ wrapper)
  const tableRegex =
    /(?:\$\$\s*|\\\[\s*)?\\begin\{table\}[\s\S]*?\\end\{table\}(?:\s*\$\$|\s*\\\])?/g;

  const toMarkdownTable = (
    rows: string[],
    caption?: string,
    forceMath: boolean = false
  ) => {
    const splitRow = (row: string) => row.split("&").map((c) => c.trim());
    const escapePipes = (s: string) => s.replace(/\|/g, "\\|");
    const maybeWrapMath = (s: string) => {
      const hasDollar = /\$/.test(s);
      const content = escapePipes(s);
      if (hasDollar) return content; // already explicitly math-marked
      return forceMath && content.length > 0 ? `$${content}$` : content;
    };

    const formatCell = (cell: string) => {
      const mark = cell.match(COLSPAN_MARK);
      if (!mark) return maybeWrapMath(cell);
      return mark[0] + maybeWrapMath(cell.slice(mark[0].length).trim());
    };
    const formatRow = (row: string) =>
      `| ${splitRow(row).map(formatCell).join(" | ")} |`;

    const headerLine = formatRow(rows[0]);
    const sepLine = `| ${splitRow(rows[0])
      .map(() => "---")
      .join(" | ")} |`;
    const bodyLines = rows.slice(1).map(formatRow).join("\n");
    const captionBlock = caption ? `\n\n${caption}\n\n` : "";
    return `${captionBlock}${headerLine}\n${sepLine}\n${bodyLines}\n`;
  };

  let out = md.replace(tableRegex, (match) => {
    try {
      // Extract caption if present
      const captionMatch = match.match(/\\caption\{([^}]*)\}/);
      const caption = captionMatch ? captionMatch[1] : "";

      // Extract tabular content
      const tabularMatch = match.match(
        /\\begin\{tabular\}(?:\[[^\]]*\])?\{[^}]*\}([\s\S]*?)\\end\{tabular\}/
      );
      if (!tabularMatch) return match;

      const tabularContent = expandLatexSpans(tabularMatch[1], "markdown-cell");

      // Split by \hline or \\ to get rows
      const rows = tabularContent
        .split(/\\hline|\\\\/)
        .map((row) => row.trim())
        .filter((row) => row.length > 0);

      if (rows.length === 0) return match;

      // Determine if the entire match was wrapped in $$ or \[ ... \] (display math)
      const inMathBlock =
        /^\s*\$\$/.test(match) ||
        /\$\$\s*$/.test(match) ||
        /^\s*\\\[/.test(match) ||
        /\\\]\s*$/.test(match);
      // If in math block, force inline math for all cells; otherwise leave cells as-is
      return toMarkdownTable(rows, caption, inMathBlock);
    } catch {
      // If parsing fails, return original
      return match;
    }
  });

  // 2) Convert standalone tabular environments (with or without $$ wrappers)
  const tabularRegex =
    /(?:\$\$\s*|\\\[\s*)?\\begin\{tabular\}(?:\[[^\]]*\])?\{[^}]*\}[\s\S]*?\\end\{tabular\}(?:\s*\$\$|\s*\\\])?/g;
  out = out.replace(tabularRegex, (match) => {
    try {
      // Extract inner content
      const innerMatch = match.match(
        /\\begin\{tabular\}(?:\[[^\]]*\])?\{[^}]*\}([\s\S]*?)\\end\{tabular\}/
      );
      if (!innerMatch) return match;

      const tabularContent = expandLatexSpans(innerMatch[1], "markdown-cell");
      const rows = tabularContent
        .split(/\\hline|\\\\/)
        .map((row) => row.trim())
        .filter((row) => row.length > 0);

      if (rows.length === 0) return match;

      const inMathBlock =
        /^\s*\$\$/.test(match) ||
        /\$\$\s*$/.test(match) ||
        /^\s*\\\[/.test(match) ||
        /\\\]\s*$/.test(match);
      return toMarkdownTable(rows, undefined, inMathBlock);
    } catch {
      return match;
    }
  });

  // 3) Spans left in KaTeX environments (array, aligned, …): plain cells
  out = out.replace(
    /\\begin\{(array|aligned|alignedat|matrix|[pbvBV]matrix|cases)\}[\s\S]*?\\end\{\1\}/g,
    (env) => expandLatexSpans(env, "math-cell")
  );
  // 4) Anything else (a stray \multicolumn outside a table): content only
  return /\\(?:multicolumn|multirow|cline|hhline)(?![a-zA-Z])/.test(out)
    ? expandLatexSpans(out, "content-only")
    : out;
}

// wiswiz 752 (sim, G&R stem-and-leaf steps: "De 30 getelde aantallen auto's zijn: $$21,\;28,\;31,\;...$$"): a display
// formula that is only a list of numbers is one line no screen holds (cut at the edge, or a sideways strip inside the
// sideways step pager). It becomes a line of inline numbers that wraps between them, each with its comma, same font.
// Only ",\;"-like separators split (a decimal comma such as 2{,}5 never does); 8 numbers or more.
function numberListLine(expr: string): string | null {
  const items = expr.trim().split(/\s*,\s*\\[;:, ]\s*/);
  if (items.length < 8 || !items.every((x) => /^-?[\d{},.]+$/.test(x))) return null;
  return `\n\n${items.map((x, i) => `$${x}${i < items.length - 1 ? "," : ""}$`).join(" ")}\n\n`;
}

function preprocessMarkdownMath(md: string): string {
  // First convert LaTeX tables/tabular to GFM Markdown tables
  const withConvertedTables = convertLatexTablesToMarkdown(md);

  return (
    withConvertedTables
      // Force any $$…$$ into its own paragraph
      // wiswiz 752: a value table of many columns becomes two tables under each other (lib/splitWideArray)
      .replace(/\$\$([\s\S]+?)\$\$/g, (_, expr) => numberListLine(expr) ?? proseMathLines(expr) ?? `\n\n$$\n${splitWideArrays(expr.trim())}\n$$\n\n`)
      // then still normalize \(…\) and \[…\]
      .replace(/\\\(([\s\S]+?)\\\)/g, (_m, e) => `$${e}$`)
      .replace(/\\\[([\s\S]+?)\\\]/g, (_m, e) => `$$${e}$$`)
    // now treat any (… ) as inline math
    // .replace(/\(([^)]+)\)/g, (_m, e) => `$${e}$`)
    // and any [ … ] as display math
    // .replace(/\[([^\]]+)\]/g, (_m, e) => `$$${e}$$`)
  );
}

function UnwrappedParagraph({ children }: { children?: ReactNode }) {
  return <>{children}</>;
}

// wiswiz 683 (iPhone 15 trace: a subvraag open re-parsed every step's markdown + KaTeX on each mount): one processor per
// mode, and the rendered elements per text are kept (React elements are immutable, so a new mount can reuse them)
const PROCESSORS = new Map<boolean, ReturnType<typeof makeProcessor>>();
const RENDERED = new Map<string, ReactNode>();
const RENDERED_MAX = 400;

function renderMarkdownMathContent(markdown: string, unwrapParagraph = false): ReactNode {
  const key = (unwrapParagraph ? "1" : "0") + markdown;
  const hit = RENDERED.get(key);
  if (hit !== undefined) {
    RENDERED.delete(key);
    RENDERED.set(key, hit);
    return hit;
  }
  let processor = PROCESSORS.get(unwrapParagraph);
  if (!processor) PROCESSORS.set(unwrapParagraph, (processor = makeProcessor(unwrapParagraph)));
  const out = processor.processSync(preprocessMarkdownMath(markdown)).result as ReactNode;
  RENDERED.set(key, out);
  if (RENDERED.size > RENDERED_MAX) RENDERED.delete(RENDERED.keys().next().value as string);
  return out;
}

// wiswiz 751: a line could break between an inline formula and the punctuation after it, so a step line started with
// ", t = 4" on the iPhone 15 (WebKit breaks after the inline-block formula, also before a word joiner). The formula and
// the punctuation that follows it go into one no-wrap span in the tree, before React draws it.
const PUNCT = /^[,.;:!?)]+/;
const isInlineMath = (n: any) => {
  if (n?.type !== "element") return false;
  const c = n.properties?.className;
  const arr: string[] = Array.isArray(c) ? c : c ? [String(c)] : [];
  return (arr.includes("katex") || arr.includes("math-inline")) && !arr.includes("katex-display") && !arr.includes("math-display");
};
function glueMathPunctuation(tree: any) {
  const walk = (node: any) => {
    const kids = node?.children;
    if (!Array.isArray(kids)) return;
    for (let i = 0; i < kids.length; i++) {
      const k = kids[i];
      const next = kids[i + 1];
      if (isInlineMath(k) && next?.type === "text" && PUNCT.test(next.value)) {
        const p = next.value.match(PUNCT)[0];
        next.value = next.value.slice(p.length);
        kids[i] = { type: "element", tagName: "span", properties: { style: "white-space:nowrap" }, children: [k, { type: "text", value: p }] };
        if (!next.value) kids.splice(i + 1, 1);
      } else walk(k);
    }
  };
  walk(tree);
}

function makeProcessor(unwrapParagraph: boolean) {
  const runtime = unwrapParagraph
    ? {
        ...production,
        components: {
          p: UnwrappedParagraph
        }
      }
    : production;

  const processor = unified()
    .use(remarkParse)
    .use(remarkMath) // parse $…$ and $$…$$ into math nodes
    .use(remarkGfm) // enable GFM tables from our prepass
    // allow the <span class="katex">…</span> that rehype-katex emits
    .use(remarkRehype, {
      allowDangerousHtml: true
    })
    .use(rehypeRaw) // turn raw HTML back into AST nodes
    .use(rehypeKatex) // render math nodes → HTML
    .use(() => applyLatexColspan) // merge \multicolumn cells back into one cell
    // ensure generated tables keep the styling class
    .use(function addLatexTableClass() {
      return (tree: any) => {
        const walk = (node: any) => {
          if (!node || typeof node !== "object") return;
          if (node.type === "element" && node.tagName === "table") {
            node.properties = node.properties || {};
            const cls = node.properties.className || [];
            const arr = Array.isArray(cls) ? cls : [cls];
            if (!arr.includes("latex-table")) arr.push("latex-table");
            node.properties.className = arr;
          }
          const children = (node as any).children;
          if (Array.isArray(children)) {
            for (const child of children) walk(child);
          }
        };
        walk(tree);
      };
    })
    .use(() => glueMathPunctuation)
    // @ts-ignore
    .use(rehypeReact, runtime);

  return processor;
}

export function MarkdownMathInline({ markdown }: { markdown: string }) {
  const rootRef = useRef<HTMLSpanElement>(null);
  useScaleDisplayMath(rootRef);

  const content = useMemo(
    () => renderMarkdownMathContent(markdown, true),
    [markdown]
  );

  return (
    <span ref={rootRef} className="markdown-math-inline">
      {content}
    </span>
  );
}

export default function MarkdownMath({ markdown }: { markdown: string }) {
  // Defer markdown processing during rapid updates (streaming)
  // React will show stale content briefly while processing new content in background
  // This prevents browser crashes from processing every 100ms streaming update
  const deferredMarkdown = useDeferredValue(markdown);
  const rootRef = useRef<HTMLDivElement>(null);
  useScaleDisplayMath(rootRef);

  const content = useMemo(
    () => renderMarkdownMathContent(deferredMarkdown),
    [deferredMarkdown]
  );

  return (
    <div id="markdown_math" ref={rootRef}>
      {content}
    </div>
  );
}
